import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { NotificationChannel } from '@/core/notifications/model';

const COLLAPSED_LINES = 4;

function ChannelInstructions({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (el) setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    // measured once against the collapsed height on mount; not re-measured on expand/collapse
  }, [text]);

  return (
    <div className="mb-3">
      <div
        ref={contentRef}
        className="mutedText"
        style={{
          fontSize: 13,
          maxHeight: expanded ? 'none' : `${COLLAPSED_LINES * 1.4}em`,
          lineHeight: '1.4em',
          overflow: 'hidden',
        }}
      >
        <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
      </div>
      {(isTruncated || expanded) && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mt-1"
          style={{ fontSize: 12 }}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

const LABEL_MAX_LENGTH = 100;

export default function ContactFormFields({
  channels,
  channelKey,
  channel,
  fieldValues,
  label,
  onChannelChange,
  onFieldChange,
  onLabelChange,
  hideChannelSelect,
}: {
  channels: NotificationChannel[];
  channelKey: string;
  channel: NotificationChannel | undefined;
  fieldValues: Record<string, string>;
  // Omit (along with onLabelChange) for a channel that never supports it —
  // the input only ever renders when channel?.supportsLabel is true anyway.
  label?: string;
  onChannelChange: (key: string) => void;
  onFieldChange: (key: string, value: string) => void;
  onLabelChange?: (value: string) => void;
  // The channel is fixed and already named elsewhere (e.g. the inline
  // "Add new {channel} contact" row inside the wizard) — showing a one-option
  // select there would just be redundant clutter.
  hideChannelSelect?: boolean;
}) {
  return (
    <>
      {!hideChannelSelect && (
        <div className="mb-3">
          <label className="form-label">Channel</label>
          <select
            className="form-select"
            value={channelKey}
            onChange={event => onChannelChange(event.target.value)}
          >
            {channels.map(c => (
              <option key={c.key} value={c.key}>
                {c.displayName}
              </option>
            ))}
          </select>
        </div>
      )}

      {channel?.instructions && (
        <ChannelInstructions key={channel.key} text={channel.instructions} />
      )}

      {channel?.supportsLabel && (
        <div className="mb-3">
          <label className="form-label">Name</label>
          <input
            className="form-control"
            placeholder="Team alerts"
            maxLength={LABEL_MAX_LENGTH}
            value={label ?? ''}
            onChange={event => onLabelChange?.(event.target.value)}
          />
          <div className="form-text">A friendly name to help you recognize this contact later.</div>
        </div>
      )}

      {channel?.configFields.map(field => (
        <div className="mb-3" key={field.key}>
          <label className="form-label">
            {field.name}
            {field.required && <span className="text-danger"> *</span>}
          </label>
          <input
            className="form-control"
            value={fieldValues[field.key] ?? ''}
            onChange={event => onFieldChange(field.key, event.target.value)}
          />
          {field.description && <div className="form-text">{field.description}</div>}
        </div>
      ))}
    </>
  );
}
